/*******************************************************
 * This component represents the 3rd step in the current
 * checkout process at the time of writing. It presents
 * to the user the current billing settings saved on
 * a cart and allows the user to change them.
 * Currently, billing address is the only
 * setting this component handles.
 *
 * @format
 */

import {CartCheckoutAddressModal} from 'frontier/cart/components/addresses/form_container';
import {useAddressDelete} from 'frontier/cart/components/addresses/use_address_delete';
import {prettyAddress, singlePrettyAddress} from 'frontier/cart/helpers/format';
import {PoForm} from 'frontier/cart/components/cart/po_form';
import {Button, FilterInput} from '@sttark/design-system';
import {
    CartCheckoutContext,
    PAYMENT_STEP,
    BILLING_STEP
} from 'frontier/cart/components/cart/cart_checkout_context';
import React, {useState, useContext} from 'react';
import {IfElse, If} from 'frontier-utilities';

// styles
import './billing_step.scss';

export const BillingStep = () => {
    const ctx = useContext(CartCheckoutContext);
    const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
    const [editingAddressId, setEditingAddressId] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const {deleteAddressById} = useAddressDelete();
    const stepIsCompleted = ctx.currentStep > BILLING_STEP;

    const billingAddressError = ctx.validationErrors?.billing?.billing_address_id || undefined;
    return (
        <>
            {!stepIsCompleted && (
                <div className="billing-step-cs28">
                    <div className="order-container-overview">
                        <div
                            className={`${
                                ctx.addresses.length > 0
                                    ? 'billing-select-label d-flex align-items-center justify-content-between'
                                    : ''
                            }`}>
                            <IfElse condition={!!ctx.cart.billing_address_id}>
                                <h6 className="color-accent-primary m-0">
                                    <i className="fa fa-check me-2" />
                                    1. Billing address
                                </h6>
                                <span>1. Choose billing address</span>
                            </IfElse>

                            <Button
                                onClick={setIsAddingNewAddress.bind(this, true)}
                                className="container_add-address-btn d-flex align-items-center add-address-btn"
                                label="Add new address"
                                secondary
                                small
                            />
                            {isAddingNewAddress && (
                                <CartCheckoutAddressModal
                                    formData={{is_shipping_address: false}}
                                    onSaveComplete={({address}) => {
                                        ctx.onChange({
                                            addresses: {$push: [address]},
                                            cart: {billing_address_id: {$set: address.id}}
                                        });
                                        setIsAddingNewAddress(false);
                                    }}
                                    onClose={setIsAddingNewAddress.bind(this, false)}
                                />
                            )}

                            {editingAddressId && (
                                <CartCheckoutAddressModal
                                    formData={ctx.addresses.find(({id}) => id === editingAddressId)}
                                    onSaveComplete={({address}) => {
                                        const addressIndex = ctx.addresses.findIndex(
                                            ({id}) => id === address.id
                                        );
                                        ctx.onChange({
                                            cart: {billing_address_id: {$set: address.id}},
                                            addresses: {$splice: [[addressIndex, 1, address]]}
                                        });
                                        setEditingAddressId(false);
                                    }}
                                    onClose={setEditingAddressId.bind(this, false)}
                                />
                            )}
                        </div>
                        <div className="mt-2 single-shipment-methods-11kt">
                            <If condition={ctx.addresses.length > 0}>
                                <FilterInput
                                    listItemsToShow={4}
                                    error={billingAddressError}
                                    placeholder="Search and select existing addresses"
                                    value={singlePrettyAddress(
                                        ctx.addresses.find(
                                            ({id}) => id === ctx.cart.billing_address_id
                                        ),
                                        'billing'
                                    )}
                                    onChange={value => {
                                        setIsUpdating(true);
                                        ctx.onChange({
                                            cart: {billing_address_id: {$set: value}},
                                            validationErrors: {$set: {}}
                                        });
                                    }}
                                    label="*Billing Address"
                                    options={
                                        prettyAddress(
                                            ctx.addresses,
                                            false,
                                            setEditingAddressId,
                                            deleteAddressId => {
                                                const addressIndex = ctx.addresses.findIndex(
                                                    ({id}) => id === deleteAddressId
                                                );
                                                if (
                                                    ctx.cart.billing_address_id === deleteAddressId
                                                ) {
                                                    ctx.onChange({
                                                        addresses: {$splice: [[addressIndex, 1]]},
                                                        cart: {billing_address_id: {$set: false}}
                                                    });
                                                } else {
                                                    ctx.onUpdateWithoutSave({
                                                        addresses: {$splice: [[addressIndex, 1]]}
                                                    });
                                                }
                                                deleteAddressById(deleteAddressId);
                                            }
                                        ).addresses
                                    }
                                    name="select_address"
                                />
                                <If condition={isUpdating && !ctx.cart.billing_address_id}>
                                    <div className="color-accent-primary">
                                        <i className="fa fa-spinner fa-spin" /> saving...
                                    </div>
                                </If>
                            </If>
                        </div>
                        <PoForm onChange={ctx.onChange} cart={ctx.cart} newestds />
                    </div>

                    <Button
                        onClick={() => ctx.onSetCurrentStep(PAYMENT_STEP)}
                        label="Save & Continue"
                        className={'mt-5'}
                        primary
                    />
                </div>
            )}

            {stepIsCompleted && (
                <div className="d-flex align-items-center justify-content-start completed-step-meta">
                    <div className="my-0 me-5">
                        <p className="m-0">
                            Bills to: <b>{ctx.cart.billing_first_name}</b> (
                            {ctx.cart.billing_company})
                        </p>
                        <p className="m-0">
                            {ctx.cart.billing_address1} {ctx.cart.billing_address2}
                        </p>

                        <p className="m-0">
                            {ctx.cart.billing_city}, {ctx.cart.billing_state} {ctx.cart.billing_zip}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};
